// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient, WithId } from '@medplum/core';
import type { Bundle, Identifier, Patient } from '@medplum/fhirtypes';
import { customAlphabet } from 'nanoid';
import type { PolarisEnv } from './env';

/**
 * Polaris Sleep Medical Record Number (MRN).
 *
 * Format: `PSL-XXXXXX` in production, `PSL-TEST-XXXXXX` elsewhere. The 6-char
 * suffix is drawn from a Crockford-style alphabet that omits 0/1/I/L/O. The
 * suffix space is ~10^9; `createPatientWithMrn` retries on collision to keep
 * the uniqueness invariant.
 *
 * Stored on `Patient.identifier` with the FHIR `MR` type code so Medplum
 * patient search and any future HL7 v2 bridge recognise the slot.
 */
export const MRN_SYSTEM = 'http://polarissleep.com/mrn';

export const MRN_TYPE_CODING = {
  system: 'http://terminology.hl7.org/CodeSystem/v2-0203',
  code: 'MR',
  display: 'Medical Record Number',
} as const;

const MRN_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ';
const MRN_SUFFIX_LENGTH = 6;
const generateSuffix = customAlphabet(MRN_ALPHABET, MRN_SUFFIX_LENGTH);

const PROD_PREFIX = 'PSL-';
const NON_PROD_PREFIX = 'PSL-TEST-';

export const MRN_REGEX = /^PSL-[2-9A-HJ-NP-Z]{6}$/;
export const MRN_TEST_REGEX = /^PSL-TEST-[2-9A-HJ-NP-Z]{6}$/;

export class MrnCollisionRetryExhaustedError extends Error {
  readonly attempts: number;
  constructor(attempts: number) {
    super(`Could not allocate a unique MRN after ${attempts} attempts`);
    this.name = 'MrnCollisionRetryExhaustedError';
    this.attempts = attempts;
  }
}

function isProductionEnv(env: PolarisEnv | string | undefined): boolean {
  return env === 'production';
}

/**
 * Generates a fresh MRN. Production emits `PSL-XXXXXX`; every non-prod env
 * (`dev-*`, `e2e`, `preview-*`, `staging`) emits `PSL-TEST-XXXXXX` so leaked
 * test data is recognisable at a glance.
 */
export function generateMrn(env: PolarisEnv | string | undefined): string {
  const prefix = isProductionEnv(env) ? PROD_PREFIX : NON_PROD_PREFIX;
  return `${prefix}${generateSuffix()}`;
}

/**
 * Validates an MRN string against the env-appropriate format. Production only
 * accepts the bare `PSL-` prefix; non-prod accepts either, since real
 * production data may need to be loaded into a non-prod env (e.g. for
 * debugging) without rewriting MRNs.
 */
export function isValidMrn(value: string, env: PolarisEnv | string | undefined): boolean {
  if (isProductionEnv(env)) {
    return MRN_REGEX.test(value);
  }
  return MRN_REGEX.test(value) || MRN_TEST_REGEX.test(value);
}

export function assertValidMrn(value: string, env: PolarisEnv | string | undefined): void {
  if (!isValidMrn(value, env)) {
    throw new Error(`Invalid MRN "${value}" for env="${env ?? ''}"`);
  }
}

export function buildMrnIdentifier(value: string): Identifier {
  return {
    use: 'usual',
    type: { coding: [{ ...MRN_TYPE_CODING }] },
    system: MRN_SYSTEM,
    value,
  };
}

export function getMrn(patient: Patient | undefined): string | undefined {
  return patient?.identifier?.find((i) => i.system === MRN_SYSTEM)?.value;
}

/**
 * Throws if the incoming Patient violates the MRN invariants:
 *   - removes a previously-stored MRN
 *   - changes a previously-stored MRN to a different value
 *   - introduces an MRN that doesn't match the env-appropriate format
 *
 * Other identifier changes pass through. Call this from the application layer
 * before issuing `medplum.updateResource(patient)` so the error surfaces at the
 * caller's stack rather than from a Subscription bot.
 */
export function assertMrnUnchanged(stored: Patient, incoming: Patient, env: PolarisEnv | string | undefined): void {
  const storedMrn = getMrn(stored);
  const incomingMrn = getMrn(incoming);

  if (incomingMrn && !isValidMrn(incomingMrn, env)) {
    throw new Error(`Invalid MRN "${incomingMrn}" for env="${env ?? ''}"`);
  }
  if (storedMrn && !incomingMrn) {
    throw new Error(`MRN ${storedMrn} cannot be removed from Patient/${stored.id ?? '<unknown>'}. MRNs are immutable.`);
  }
  if (storedMrn && incomingMrn && storedMrn !== incomingMrn) {
    throw new Error(
      `MRN is immutable for Patient/${stored.id ?? '<unknown>'}. Stored="${storedMrn}", incoming="${incomingMrn}".`
    );
  }
}

/**
 * Returns a copy of `patient` with `mrn` set as the MRN identifier. Any prior
 * identifier in the MRN slot is replaced.
 */
export function withMrnIdentifier(patient: Patient, mrn: string): Patient {
  const others: Identifier[] = (patient.identifier ?? []).filter((i) => i.system !== MRN_SYSTEM);
  return { ...patient, identifier: [buildMrnIdentifier(mrn), ...others] };
}

export interface CreatePatientWithMrnOptions {
  /** Maximum collision retries before giving up. Defaults to 5. */
  maxAttempts?: number;
}

/**
 * Creates a Patient with a generated MRN, retrying on collision. Uses a
 * one-entry transaction Bundle whose `ifNoneExist` keys off the new MRN:
 * 201 = created, 200 = MRN already taken (regenerate and retry).
 *
 * Call this first when bundling related resources, then issue a second
 * transaction referencing `Patient/<patient.id>`. Bundling the conditional
 * create with related resources is unsafe: on collision the `urn:uuid:`
 * resolves to the existing Patient and Coverage gets attached to them.
 */
export async function createPatientWithMrn(
  medplum: MedplumClient,
  basePatient: Patient,
  env: PolarisEnv | string | undefined,
  options: CreatePatientWithMrnOptions = {}
): Promise<WithId<Patient>> {
  const maxAttempts = options.maxAttempts ?? 5;

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const mrn = generateMrn(env);
    const patient = withMrnIdentifier(basePatient, mrn);

    const bundle: Bundle = {
      resourceType: 'Bundle',
      type: 'transaction',
      entry: [
        {
          fullUrl: `urn:uuid:${crypto.randomUUID()}`,
          resource: patient,
          request: {
            method: 'POST',
            url: 'Patient',
            ifNoneExist: `identifier=${MRN_SYSTEM}|${mrn}`,
          },
        },
      ],
    };

    const response = await medplum.executeBatch(bundle);
    const entry = response.entry?.[0];
    const status = entry?.response?.status;

    if (status?.startsWith('201')) {
      const created = entry?.resource as Patient | undefined;
      if (created?.id) {
        return created as WithId<Patient>;
      }
      const location = entry?.response?.location;
      const id = location?.split('/')[1];
      if (id) {
        return medplum.readResource('Patient', id);
      }
      throw new Error('Patient created but server response is missing id and location');
    }
    // status 200 → MRN already exists, regenerate and retry.
  }

  throw new MrnCollisionRetryExhaustedError(maxAttempts);
}
